import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, MapPin, Clock, Trophy, ChevronLeft, ChevronRight } from 'lucide-react';
import { Calendar as CalendarComponent } from '../components/ui/calendar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MatchesPage = () => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches`);
      setMatches(response.data);
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMatches = matches.filter(match => {
    if (filter === 'upcoming') return !match.is_completed;
    if (filter === 'completed') return match.is_completed;
    return true;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-PT', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  const getMatchDates = () => {
    return matches.map(m => new Date(m.date));
  };

  const getMatchesForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return matches.filter(m => m.date === dateStr);
  };

  return (
    <div className="min-h-screen pt-20" data-testid="matches-page">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 text-center section-padding">
          <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Época 2024/2025</span>
          <h1 className="font-heading text-5xl md:text-7xl font-black mt-2">JOGOS</h1>
        </div>
      </section>

      {/* Controls */}
      <section className="bg-[#0A0A0A] border-b border-white/10 sticky top-20 z-40">
        <div className="container-custom section-padding">
          <div className="flex flex-wrap items-center justify-between gap-4 py-4">
            <div className="flex gap-2">
              {[
                { key: 'all', label: 'Todos' },
                { key: 'upcoming', label: 'Próximos' },
                { key: 'completed', label: 'Resultados' },
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-4 py-2 text-xs uppercase tracking-widest font-bold transition-all ${
                    filter === f.key
                      ? 'bg-white text-black'
                      : 'text-gray-400 hover:text-white'
                  }`}
                  data-testid={`filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setView('list')}
                className={`px-4 py-2 text-xs uppercase tracking-widest font-bold transition-all ${
                  view === 'list' ? 'bg-white text-black' : 'border border-white/20 text-gray-400'
                }`}
                data-testid="view-list"
              >
                Lista
              </button>
              <button
                onClick={() => setView('calendar')}
                className={`px-4 py-2 text-xs uppercase tracking-widest font-bold transition-all ${
                  view === 'calendar' ? 'bg-white text-black' : 'border border-white/20 text-gray-400'
                }`}
                data-testid="view-calendar"
              >
                Calendário
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 section-padding" data-testid="matches-content">
        <div className="container-custom">
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 h-32 skeleton" />
              ))}
            </div>
          ) : view === 'list' ? (
            /* List View */
            filteredMatches.length > 0 ? (
              <div className="space-y-4">
                {filteredMatches.map((match) => (
                  <div 
                    key={match.id} 
                    className="match-card"
                    data-testid={`match-${match.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                      {/* Date & Competition */}
                      <div className="md:w-1/4">
                        <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                          <Calendar size={14} />
                          <span>{formatDate(match.date)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-500 text-xs">
                          <Clock size={12} />
                          <span>{match.time}</span>
                        </div>
                        <span className="inline-block mt-2 text-xs uppercase tracking-widest text-gray-500">
                          {match.competition}
                        </span>
                      </div>

                      {/* Teams & Score */}
                      <div className="flex-1 flex items-center justify-center gap-4">
                        <div className="flex-1 text-right">
                          <div className="flex items-center justify-end gap-3">
                            <span className="font-heading font-bold text-lg">
                              {match.home_away === 'home' ? 'GDR ESPORÕES' : match.opponent}
                            </span>
                            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                              <span className="font-heading font-black text-black text-xs">
                                {match.home_away === 'home' ? 'GDR' : match.opponent.substring(0, 3).toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="px-4">
                          {match.is_completed ? (
                            <div className="score-display text-3xl">
                              <span>{match.home_score}</span>
                              <span className="text-gray-600 mx-2">-</span>
                              <span>{match.away_score}</span>
                            </div>
                          ) : (
                            <span className="font-mono text-xl text-gray-500">VS</span>
                          )}
                        </div>

                        <div className="flex-1 text-left">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                              <span className="font-heading font-bold text-gray-400 text-xs">
                                {match.home_away === 'away' ? 'GDR' : match.opponent.substring(0, 3).toUpperCase()}
                              </span>
                            </div>
                            <span className="font-heading font-bold text-lg">
                              {match.home_away === 'away' ? 'GDR ESPORÕES' : match.opponent}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Location */}
                      <div className="md:w-1/4 md:text-right">
                        <div className="flex items-center gap-2 md:justify-end text-gray-400 text-sm">
                          <MapPin size={14} />
                          <span>{match.location}</span>
                        </div>
                        {match.is_completed && (
                          <div className="mt-2">
                            {match.home_away === 'home' ? (
                              match.home_score > match.away_score ? (
                                <span className="text-green-500 text-xs uppercase tracking-widest font-bold">Vitória</span>
                              ) : match.home_score < match.away_score ? (
                                <span className="text-red-500 text-xs uppercase tracking-widest font-bold">Derrota</span>
                              ) : (
                                <span className="text-gray-500 text-xs uppercase tracking-widest font-bold">Empate</span>
                              )
                            ) : (
                              match.away_score > match.home_score ? (
                                <span className="text-green-500 text-xs uppercase tracking-widest font-bold">Vitória</span>
                              ) : match.away_score < match.home_score ? (
                                <span className="text-red-500 text-xs uppercase tracking-widest font-bold">Derrota</span>
                              ) : (
                                <span className="text-gray-500 text-xs uppercase tracking-widest font-bold">Empate</span>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-24 border border-white/10">
                <Calendar size={48} className="mx-auto mb-4 text-gray-600" />
                <p className="text-gray-500">Nenhum jogo encontrado</p>
                <p className="text-gray-600 text-sm mt-2">Os jogos serão adicionados em breve</p>
              </div>
            )
          ) : (
            /* Calendar View */
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="bg-[#0A0A0A] border border-white/10 p-6">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-none"
                  modifiers={{
                    hasMatch: getMatchDates()
                  }}
                  modifiersStyles={{
                    hasMatch: { 
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      fontWeight: 'bold'
                    }
                  }}
                />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold mb-4">
                  {selectedDate.toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long' })}
                </h3>
                {getMatchesForDate(selectedDate).length > 0 ? (
                  <div className="space-y-4">
                    {getMatchesForDate(selectedDate).map((match) => (
                      <div key={match.id} className="match-card">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-heading font-bold">GDR Esporões vs {match.opponent}</p>
                            <p className="text-gray-400 text-sm">{match.time} • {match.location}</p>
                          </div>
                          {match.is_completed && (
                            <div className="score-display text-2xl">
                              {match.home_score} - {match.away_score}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border border-white/10 p-8 text-center">
                    <p className="text-gray-500">Sem jogos nesta data</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MatchesPage;
